/**
 * Custom progress bar — smooth fill + milestone confetti.
 * Re-inits after cart drawer / cart summary section HTML swaps.
 */
(function () {
  const controllers = new Map();
  const sessionState = new Map();
  const FINAL_FILL_FALLBACK_MS = 1150;
  const UNLOCK_HANDOFF_MS = 1200;
  let documentListenersBound = false;
  let drawerCompletionUntil = 0;

  function prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function readConfig(wrapper) {
    return {
      isCartLike: wrapper.dataset.pbContext === 'cart' || wrapper.dataset.pbContext === 'drawer',
      isDrawer: wrapper.dataset.pbContext === 'drawer',
      threshold: Number(wrapper.dataset.pbThreshold) || 99,
      maxThreshold: Number(wrapper.dataset.pbMaxThreshold) || Number(wrapper.dataset.pbThreshold) || 99,
      tier2Threshold: Number(wrapper.dataset.pbTier2Threshold) || 50,
      tier2Label: wrapper.dataset.pbTier2Label || '3,90 €',
      sampleThreshold: Number(wrapper.dataset.pbSampleThreshold) || 120,
      sampleEnabled: wrapper.dataset.pbSampleEnabled === 'true',
      animationsEnabled: wrapper.dataset.pbAnimationsEnabled !== 'false',
      rewardName: wrapper.dataset.pbRewardName || 'la livraison gratuite',
      tier2RewardName:
        wrapper.dataset.pbTier2RewardName ||
        'la livraison à ' + (wrapper.dataset.pbTier2Label || '3,90 €'),
      sampleRewardName: wrapper.dataset.pbSampleRewardName || 'un échantillon offert',
      successMessage: wrapper.dataset.pbSuccessMessage || 'Livraison offerte !',
      sampleSuccessMessage: wrapper.dataset.pbSampleSuccessMessage || 'Échantillon offert débloqué !',
      sampleProgressEligible: wrapper.dataset.pbSampleProgressEligible || '',
      confettiColors: (wrapper.dataset.pbConfettiColors || '#ffef53,#093027,#c0925d,#ffd800,#ffffff').split(',')
    };
  }

  function loadState(id) {
    if (!sessionState.has(id)) {
      sessionState.set(id, {
        reachedMilestones: new Set(),
        pendingCelebrations: new Set(),
        hasInitializedMilestones: false,
        lastProgressPct: null,
        finalSequenceDone: false
      });
    }
    return sessionState.get(id);
  }

  function saveState(id, state) {
    sessionState.set(id, state);
  }

  function cartTotalCents(cart) {
    if (!cart) return 0;
    if (cart.total_price > 0) return cart.total_price;
    if (cart.items_subtotal_price > 0) return cart.items_subtotal_price;
    if (Array.isArray(cart.items)) {
      return cart.items.reduce((sum, item) => {
        return sum + (item.final_line_price != null ? item.final_line_price : item.line_price || 0);
      }, 0);
    }
    return 0;
  }

  function fetchCart() {
    const cartBase =
      window.theme && window.theme.routes && window.theme.routes.cart
        ? window.theme.routes.cart
        : '/cart';
    const url = /\.js(\?|$)/.test(cartBase) ? cartBase : cartBase + '.js';
    return fetch(url, {
      credentials: 'same-origin',
      cache: 'no-store',
      headers: { Accept: 'application/json' }
    }).then((res) => {
      if (!res.ok) throw new Error('cart.js HTTP ' + res.status);
      return res.json();
    });
  }

  function formatMoneyFromCents(cents) {
    if (window.theme && window.theme.settings && window.theme.settings.moneyFormat) {
      const value = (cents / 100).toFixed(2);
      return window.theme.settings.moneyFormat.replace(/\{\{\s*(\w+)\s*\}\}/, value);
    }
    return (cents / 100).toFixed(2) + '€';
  }

  function progressScale(progressPercentage) {
    return 'scaleX(' + (Math.max(0, Math.min(progressPercentage, 100)) / 100).toFixed(4) + ')';
  }

  class ProgressBarController {
    constructor(wrapper) {
      this.wrapper = wrapper;
      this.id = wrapper.id;
      this.config = readConfig(wrapper);
      this.state = loadState(this.id);
      this.pendingTimer = null;
      this.finalHideTimer = null;
      this.primeInitialComplete();
      this.primeFillFromState();
    }

    primeInitialComplete() {
      if (!this.config.isDrawer) return;
      if (this.wrapper.dataset.pbInitiallyComplete !== 'true') return;

      this.state.finalSequenceDone = true;
      this.wrapper.classList.add('hidden');
      this.wrapper.classList.remove('is-completing-final');
      this.setDrawerProgressVisibility(false);
      saveState(this.id, this.state);
    }

    primeFillFromState() {
      const progressFill = this.wrapper.querySelector('[data-progress-fill]');
      if (!progressFill) return;

      const fromPct = this.state.lastProgressPct;
      if (fromPct == null) return;

      progressFill.style.transition = 'none';
      progressFill.style.transform = progressScale(fromPct);
      progressFill.offsetHeight;
      progressFill.style.removeProperty('transition');
    }

    cartFromDataset() {
      const cents = Number(this.wrapper.dataset.cartTotalCents);
      const paid = Number(this.wrapper.dataset.paidSubtotalCents);
      const count = Number(this.wrapper.dataset.cartItemCount);
      if (Number.isNaN(cents)) return null;
      return {
        total_price: cents,
        items_subtotal_price: cents,
        paid_subtotal: Number.isNaN(paid) ? cents : paid,
        item_count: Number.isNaN(count) ? 0 : count
      };
    }

    getSampleState(cart) {
      const { sampleEnabled, sampleThreshold } = this.config;
      if (!window.HugyFreeSampleState || !sampleEnabled) return null;
      return window.HugyFreeSampleState.getFreeSampleState(cart, {
        thresholdCents: sampleThreshold * 100,
        sampleProductIds: this.wrapper.dataset.sampleProductIds || ''
      });
    }

    paidSubtotalCents(cart) {
      const items = cart?.items;
      if (Array.isArray(items) && items.length > 0) {
        const state = this.getSampleState(cart);
        if (state) return state.paidSubtotal;
        return cartTotalCents(cart);
      }

      if (cart?.paid_subtotal != null && !Number.isNaN(cart.paid_subtotal)) {
        return cart.paid_subtotal;
      }

      const paidFromDataset = Number(this.wrapper.dataset.paidSubtotalCents);
      if (!Number.isNaN(paidFromDataset)) return paidFromDataset;

      return cartTotalCents(cart);
    }

    syncDataset(cart) {
      if (!cart) return;
      const paid = this.paidSubtotalCents(cart);
      this.wrapper.dataset.cartTotalCents = String(cartTotalCents(cart));
      this.wrapper.dataset.paidSubtotalCents = String(paid);
      this.wrapper.dataset.cartItemCount = String(cart.item_count || 0);

      if (Array.isArray(cart.items) && cart.items.length > 0) {
        const state = this.getSampleState(cart);
        if (state) this.wrapper.dataset.pbFreeApplied = String(state.freeApplied);
      }
    }

    spawnConfetti(milestone) {
      if (prefersReducedMotion() || !this.config.animationsEnabled) return;

      const { confettiColors } = this.config;
      for (let i = 0; i < 12; i += 1) {
        const particle = document.createElement('span');
        particle.className = 'custom-progress-bar-confetti';
        particle.style.setProperty('--pb-confetti-x', ((Math.random() - 0.5) * 34).toFixed(1) + 'px');
        particle.style.setProperty('--pb-confetti-y', (-10 - Math.random() * 26).toFixed(1) + 'px');
        particle.style.setProperty('--pb-confetti-r', (Math.random() * 540 - 270).toFixed(0) + 'deg');
        particle.style.setProperty('--pb-confetti-d', (0.5 + Math.random() * 0.35).toFixed(2) + 's');
        particle.style.backgroundColor = confettiColors[i % confettiColors.length];
        if (Math.random() > 0.45) particle.style.borderRadius = '50%';
        milestone.appendChild(particle);
        particle.addEventListener('animationend', () => particle.remove(), { once: true });
      }
    }

    celebrateMilestone(milestone) {
      if (!this.config.animationsEnabled) return;
      if (milestone.classList.contains('is-celebrating')) return;
      milestone.classList.add('is-celebrating');
      this.spawnConfetti(milestone);
      milestone.addEventListener('animationend', () => milestone.classList.remove('is-celebrating'), { once: true });
    }

    setDrawerProgressVisibility(visible) {
      const parent = this.wrapper.closest('.cart-drawer__delivery-progress');
      if (parent) parent.classList.toggle('hidden', !visible);
    }

    completeDrawerFinalSequence() {
      if (this.state.finalSequenceDone) return;

      if (this.finalHideTimer) clearTimeout(this.finalHideTimer);
      this.finalHideTimer = null;

      this.wrapper.classList.add('hidden');
      this.wrapper.classList.remove('is-completing-final');
      this.setDrawerProgressVisibility(false);
      this.state.finalSequenceDone = true;
      drawerCompletionUntil = 0;
      saveState(this.id, this.state);

      if (this.config.sampleEnabled) {
        document.dispatchEvent(new CustomEvent('on:progress-bar:final-milestone-complete'));
      }
    }

    scheduleDrawerFinalHide(progressFill) {
      if (this.finalHideTimer) clearTimeout(this.finalHideTimer);

      const finish = () => {
        this.finalHideTimer = null;
        this.completeDrawerFinalSequence();
      };

      if (prefersReducedMotion() || !this.config.animationsEnabled || !progressFill) {
        finish();
        return;
      }

      const onTransitionEnd = (event) => {
        if (event.propertyName !== 'transform') return;
        progressFill.removeEventListener('transitionend', onTransitionEnd);
        finish();
      };

      progressFill.addEventListener('transitionend', onTransitionEnd);
      this.finalHideTimer = setTimeout(() => {
        progressFill.removeEventListener('transitionend', onTransitionEnd);
        finish();
      }, FINAL_FILL_FALLBACK_MS);
    }

    celebratePending() {
      if (!this.config.isDrawer) return;

      this.wrapper.querySelectorAll('[data-milestone-tier]').forEach((milestone) => {
        const tier = milestone.dataset.milestoneTier;
        if (!this.state.pendingCelebrations.has(tier)) return;
        this.celebrateMilestone(milestone);
        this.state.pendingCelebrations.delete(tier);
      });

      saveState(this.id, this.state);
    }

    setFillWidth(progressFill, progressPercentage, cart) {
      const target = Math.max(0, Math.min(progressPercentage, 100));
      const reduced = prefersReducedMotion() || !this.config.animationsEnabled;
      const isFirstEverPaint = !this.state.hasInitializedMilestones;
      let fromPct = this.state.lastProgressPct;

      if (fromPct == null) {
        const initial = Number(progressFill.dataset.initialProgress);
        if (isFirstEverPaint) {
          fromPct = 0;
        } else {
          fromPct = !Number.isNaN(initial) ? initial : 0;
        }
      }

      progressFill.classList.add('is-animating');

      if (reduced || Math.abs(fromPct - target) < 0.05) {
        progressFill.style.transform = progressScale(target);
      } else {
        progressFill.style.transition = 'none';
        progressFill.style.transform = progressScale(fromPct);
        progressFill.offsetHeight;
        progressFill.style.removeProperty('transition');
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            progressFill.style.transform = progressScale(target);
          });
        });
      }

      if (cart?.item_count === 0) {
        this.state.lastProgressPct = null;
      } else {
        this.state.lastProgressPct = target;
      }
      saveState(this.id, this.state);
    }

    updateMilestones(cartTotal) {
      const { threshold, tier2Threshold, sampleThreshold, isDrawer } = this.config;

      this.wrapper.querySelectorAll('[data-milestone-tier]').forEach((milestone) => {
        const tier = milestone.dataset.milestoneTier;
        let reached = false;
        if (tier === 'free') {
          reached = cartTotal >= threshold;
        } else if (tier === '2') {
          reached = cartTotal >= tier2Threshold;
        } else if (tier === 'sample') {
          reached = cartTotal >= sampleThreshold;
        }

        const wasReached = this.state.reachedMilestones.has(tier);
        milestone.classList.toggle('is-reached', reached);

        if (reached) {
          if (!wasReached && this.state.hasInitializedMilestones) {
            if (isDrawer) {
              this.state.pendingCelebrations.add(tier);
            } else {
              this.celebrateMilestone(milestone);
            }
          }
          this.state.reachedMilestones.add(tier);
        } else {
          this.state.reachedMilestones.delete(tier);
          this.state.pendingCelebrations.delete(tier);
        }
      });

      this.state.hasInitializedMilestones = true;
      saveState(this.id, this.state);
    }

    updateMessage(cartTotalEuros, cart) {
      const progressMessage = this.wrapper.querySelector('[data-progress-message]');
      if (!progressMessage) return;

      const {
        isDrawer,
        sampleEnabled,
        threshold,
        tier2Threshold,
        sampleThreshold,
        rewardName,
        tier2RewardName,
        sampleRewardName,
        successMessage,
        sampleSuccessMessage,
        sampleProgressEligible,
        isCartLike
      } = this.config;

      const lastThreshold = sampleEnabled ? sampleThreshold : threshold;
      const paidCents = Math.round(cartTotalEuros * 100);

      if (isDrawer && sampleEnabled) {
        const hasItems = Array.isArray(cart?.items) && cart.items.length > 0;
        const state = hasItems ? this.getSampleState(cart) : null;
        const sampleReached = state ? state.isThresholdReached : paidCents >= sampleThreshold * 100;
        const freeApplied = state ? state.freeApplied : this.wrapper.dataset.pbFreeApplied === 'true';

        if (freeApplied) {
          progressMessage.textContent = sampleSuccessMessage;
          progressMessage.style.fontWeight = 'bold';
          return;
        }
        if (sampleReached) {
          progressMessage.textContent = sampleProgressEligible;
          progressMessage.style.fontWeight = 'normal';
          return;
        }
      }

      if (cartTotalEuros >= lastThreshold) {
        progressMessage.textContent = sampleEnabled ? sampleSuccessMessage : successMessage;
        progressMessage.style.fontWeight = 'bold';
        return;
      }

      if (isCartLike && (!cart || cart.item_count === 0)) {
        progressMessage.textContent = '';
        return;
      }

      let nextThreshold;
      let nextReward;
      let useChooseWording = false;

      if (tier2Threshold > 0 && tier2Threshold < lastThreshold && cartTotalEuros < tier2Threshold) {
        nextThreshold = tier2Threshold;
        nextReward = tier2RewardName;
      } else if (cartTotalEuros < threshold) {
        nextThreshold = threshold;
        nextReward = rewardName;
      } else if (sampleEnabled) {
        nextThreshold = sampleThreshold;
        nextReward = sampleRewardName;
        useChooseWording = isDrawer;
      } else {
        progressMessage.textContent = successMessage;
        progressMessage.style.fontWeight = 'bold';
        return;
      }

      if (!nextReward) return;

      const remainingCents = Math.max(0, Math.round(nextThreshold * 100 - paidCents));
      const amountHtml = '<strong>' + formatMoneyFromCents(remainingCents) + '</strong>';
      const rewardHtml = '<strong>' + nextReward + '</strong>';

      progressMessage.innerHTML = useChooseWording
        ? amountHtml + ' pour choisir ' + rewardHtml
        : amountHtml + ' restants pour ' + rewardHtml;
      progressMessage.style.fontWeight = 'normal';
    }

    applyCart(cart, options = {}) {
      if (!cart) return;

      const { isCartLike, threshold, maxThreshold } = this.config;
      const { animateFill = true } = options;
      const progressPercentage = maxThreshold > 0 ? Math.min((this.paidSubtotalCents(cart) / 100 / maxThreshold) * 100, 100) : 0;
      const progressFill = this.wrapper.querySelector('[data-progress-fill]');

      if (isCartLike && cart.item_count === 0) {
        if (this.finalHideTimer) clearTimeout(this.finalHideTimer);
        this.finalHideTimer = null;
        this.wrapper.classList.remove('is-completing-final');
        this.wrapper.classList.add('hidden');
        this.setDrawerProgressVisibility(false);
        this.syncDataset(cart);
        if (progressFill) progressFill.style.transform = progressScale(0);
        this.state.lastProgressPct = null;
        this.state.pendingCelebrations.clear();
        this.state.finalSequenceDone = false;
        drawerCompletionUntil = 0;
        saveState(this.id, this.state);
        return;
      }
      if (isCartLike) this.wrapper.classList.remove('hidden');

      this.syncDataset(cart);

      const cartTotal = this.paidSubtotalCents(cart) / 100;

      if (this.config.isDrawer) {
        const drawerReached = this.config.sampleEnabled && cartTotal >= this.config.sampleThreshold;
        if (drawerReached) {
          if (this.state.finalSequenceDone) {
            this.wrapper.classList.add('hidden');
            this.wrapper.classList.remove('is-completing-final');
            this.setDrawerProgressVisibility(false);
            this.syncDataset(cart);
            if (progressFill) {
              progressFill.style.transform = progressScale(100);
              progressFill.classList.add('reached');
            }
            this.updateMilestones(cartTotal);
            return;
          }

          const shouldAnimateFinal =
            animateFill &&
            this.config.animationsEnabled &&
            !prefersReducedMotion() &&
            this.state.lastProgressPct != null &&
            this.state.lastProgressPct < 99.95 &&
            this.wrapper.dataset.pbInitiallyComplete !== 'true';

          this.wrapper.classList.remove('hidden');
          if (shouldAnimateFinal) {
            this.wrapper.classList.add('is-completing-final');
          } else {
            this.wrapper.classList.remove('is-completing-final');
          }
          drawerCompletionUntil = Date.now() + (shouldAnimateFinal ? FINAL_FILL_FALLBACK_MS + UNLOCK_HANDOFF_MS : 0);

          if (progressFill) {
            this.setFillWidth(progressFill, 100, cart);
            progressFill.classList.add('reached');
          }
          this.updateMilestones(cartTotal);
          this.updateMessage(cartTotal, cart);

          if (shouldAnimateFinal) {
            this.scheduleDrawerFinalHide(progressFill);
            return;
          }

          this.completeDrawerFinalSequence();
          return;
        }

        if (this.finalHideTimer) clearTimeout(this.finalHideTimer);
        this.finalHideTimer = null;
        this.wrapper.classList.remove('is-completing-final');
        this.wrapper.classList.remove('hidden');
        this.setDrawerProgressVisibility(true);
        this.state.finalSequenceDone = false;
        drawerCompletionUntil = 0;
        saveState(this.id, this.state);

        if (this.config.sampleEnabled) {
          document.dispatchEvent(new CustomEvent('on:progress-bar:final-milestone-reset'));
        }
      }

      if (progressFill && animateFill) {
        this.setFillWidth(progressFill, progressPercentage, cart);
        progressFill.classList.toggle('reached', cartTotal >= threshold);
      } else if (progressFill) {
        progressFill.style.transform = progressScale(progressPercentage);
        progressFill.classList.toggle('reached', cartTotal >= threshold);
        this.state.lastProgressPct = progressPercentage;
        saveState(this.id, this.state);
      }

      this.updateMilestones(cartTotal);

      this.updateMessage(cartTotal, cart);
    }

    refreshFromApi() {
      return fetchCart()
        .then((cart) => this.applyCart(cart))
        .catch(() => {
          const fallback = this.cartFromDataset();
          if (fallback) this.applyCart(fallback);
        });
    }

    scheduleRefresh(delay) {
      if (this.pendingTimer) clearTimeout(this.pendingTimer);
      this.pendingTimer = setTimeout(() => this.refreshFromApi(), delay == null ? 0 : delay);
    }

    onCartChange(cart) {
      if (cart && typeof cart.item_count === 'number') {
        this.applyCart(cart);
        return;
      }

      this.scheduleRefresh(80);
    }

    onDrawerOpen() {
      if (!this.config.isDrawer) return;
      this.celebratePending();
    }
  }

  function snapshotProgressWidths() {
    controllers.forEach((ctrl) => {
      if (!ctrl.id) return;

      const fill = ctrl.wrapper?.querySelector?.('[data-progress-fill]');
      if (!fill) return;

      const inline = fill.style.transform.match(/scaleX\(([^)]+)\)/);
      if (inline) {
        const scale = Number(inline[1]);
        if (!Number.isNaN(scale) && scale >= 0) {
          ctrl.state.lastProgressPct = Math.round(scale * 1000) / 10;
          saveState(ctrl.id, ctrl.state);
          return;
        }
      }

      const inlineWidth = parseFloat(fill.style.width);
      if (!Number.isNaN(inlineWidth) && inlineWidth >= 0) {
        ctrl.state.lastProgressPct = inlineWidth;
        saveState(ctrl.id, ctrl.state);
        return;
      }

      const bg = fill.parentElement;
      if (!bg) return;

      const bgWidth = bg.getBoundingClientRect().width;
      const fillWidth = fill.getBoundingClientRect().width;
      if (bgWidth <= 0) return;

      ctrl.state.lastProgressPct = Math.round((fillWidth / bgWidth) * 1000) / 10;
      saveState(ctrl.id, ctrl.state);
    });
  }

  function purgeDetachedControllers() {
    controllers.forEach((ctrl, id) => {
      if (!document.getElementById(id)) controllers.delete(id);
    });
  }

  function initProgressBars(root, cart) {
    purgeDetachedControllers();

    const wrappers = !root || root === document
      ? document.querySelectorAll('.custom-progress-bar-wrapper[data-pb-context]')
      : root.querySelectorAll('.custom-progress-bar-wrapper[data-pb-context]');

    wrappers.forEach((wrapper) => {
      const id = wrapper.id;
      if (!id) return;

      let ctrl = controllers.get(id);
      if (!ctrl || ctrl.wrapper !== wrapper) {
        ctrl = new ProgressBarController(wrapper);
        controllers.set(id, ctrl);
      }

      if (cart) {
        ctrl.applyCart(cart);
      } else {
        const initialCart = ctrl.cartFromDataset();
        if (initialCart) ctrl.applyCart(initialCart);
      }
    });
  }

  function onDocumentCartChange(cart) {
    controllers.forEach((ctrl) => ctrl.onCartChange(cart));
  }

  function onCartDrawerOpen() {
    controllers.forEach((ctrl) => ctrl.onDrawerOpen());
  }

  function bindDocumentListeners() {
    if (documentListenersBound) return;
    documentListenersBound = true;

    document.addEventListener('on:cart:add', (e) => onDocumentCartChange(e.detail && e.detail.cart));
    document.addEventListener('on:line-item:change', (e) => onDocumentCartChange(e.detail && e.detail.cart));
    document.addEventListener('on:cart-drawer:after-open', onCartDrawerOpen);
  }

  window.HugyCustomProgressBar = {
    init: initProgressBars,
    snapshot: snapshotProgressWidths,
    getDrawerCompletionDelay() {
      return Math.max(0, drawerCompletionUntil - Date.now());
    },
    refresh(cart) {
      onDocumentCartChange(cart);
    }
  };

  bindDocumentListeners();

  document.addEventListener('on:progress-bar:mount', (event) => {
    initProgressBars(event.detail?.root || document, event.detail?.cart);
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initProgressBars());
  } else {
    initProgressBars();
  }
})();
